import { MENTION_TYPE } from "@/constants";
import { useMentionCardsContext } from "@/containers/MentionCardsContext";
import { AudioMentionNew, EpisodeV4, MentionV4 } from "@/types/MentionCard";
import { useMediaRef } from "media-chrome/react/media-store";
import { useCallback, useMemo, useRef } from "react";
import ActionButton from "./ActionButton";
import AudioTrack from "./AudioTrack";
import AutoSkip from "./AutoSkip";
import CurrentTimeDisplay from "./CurrentTimeDisplay";
import DurationDisplay from "./DurationDisplay";
import MentionBlip from "./MentionBlip";
import NextMentionButton from "./NextMentionButton";
import PreviousMentionButton from "./PreviousMentionButton";

/**
 * Audio element wrapper that properly releases browser resources on unmount.
 * Without this cleanup, browsers can exhaust their connection limits after
 * playing multiple audio files, causing new audio to fail to load.
 */
function AudioElement({ src }: { src: string }) {
  const mediaRef = useMediaRef();
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const mediaRefLatest = useRef(mediaRef);
  mediaRefLatest.current = mediaRef;

  const setRefs = useCallback((element: HTMLAudioElement | null) => {
    // When unmounting (element is null), clean up resources
    if (audioElementRef.current && element === null) {
      const audio = audioElementRef.current;

      // Disconnect from MediaProvider first to prevent error events
      mediaRefLatest.current(null);

      // Then clean up audio resources to release browser connections
      audio.pause();
      audio.removeAttribute("src");
      audio.load();

      audioElementRef.current = null;
      return;
    }

    audioElementRef.current = element;
    mediaRefLatest.current(element);
  }, []);

  return <audio src={src} ref={setRefs} />;
}

type AudioPlayerProps = {
  audio_url: EpisodeV4["audio_url"];
  episode_id: EpisodeV4["id"];
  length_seconds: number;
  mentions: MentionV4[];
  onPlay?: () => void;
};

function AudioControls({
  lengthSeconds,
  episodeId,
  audioUrl,
  mentions,
  onPlay,
}: {
  audioUrl: EpisodeV4["audio_url"];
  mentions: AudioMentionNew[];
  episodeId: EpisodeV4["id"];
  lengthSeconds: number;
  onPlay?: () => void;
}) {
  const { activeEpisodeId } = useMentionCardsContext();
  const isActiveEpisode = activeEpisodeId === episodeId;

  return (
    <>
      <AutoSkip mentions={mentions} episodeId={episodeId} />

      <div className="flex items-center gap-2.5">
        <div className="flex items-center space-x-0.5">
          <PreviousMentionButton disabled={!audioUrl} mentions={mentions} />
          <ActionButton
            disabled={!audioUrl}
            episodeId={episodeId}
            mentions={mentions}
            onPlay={onPlay}
          />
          <NextMentionButton disabled={!audioUrl} mentions={mentions} />
        </div>

        <CurrentTimeDisplay />

        <div className="relative flex-1">
          <AudioTrack mentions={mentions} />

          {mentions.map((mention) => {
            return (
              <MentionBlip
                episodeId={episodeId}
                mention={mention}
                key={mention.id}
              />
            );
          })}
        </div>

        <DurationDisplay lengthSeconds={lengthSeconds} />

        {audioUrl && isActiveEpisode ? <AudioElement src={audioUrl} /> : null}
      </div>
    </>
  );
}

export function AudioPlayer({
  length_seconds,
  episode_id,
  audio_url,
  mentions,
  onPlay,
}: AudioPlayerProps) {
  // Helpers
  const audioMentions = useMemo<AudioMentionNew[]>(() => {
    return mentions
      .filter(
        (m) => m.context === MENTION_TYPE.TEXT && typeof m.timing === "number"
      )
      .map((m) => {
        return {
          ...m,
          total_length: length_seconds,
        };
      })
      .toSorted(
        (a, b) =>
          (a.indices_metadata?.start_time ?? 0) -
          (b.indices_metadata?.start_time ?? 0)
      );
  }, [mentions, length_seconds]);

  if (!audioMentions.length) return null;

  return (
    <AudioControls
      episodeId={episode_id}
      audioUrl={audio_url}
      mentions={audioMentions}
      lengthSeconds={length_seconds}
      onPlay={onPlay}
    />
  );
}
